import { type ReactNode } from "react"
import { ListItemIcon, ListItemText, MenuItem, MenuList } from "@mui/material"
import { Check as CheckIcon } from "lucide-react"

import { InlineEditField } from "./inline-edit-field"

export interface InlineSelectOption {
  value: string
  label: string
}

interface InlineSelectEditorProps {
  /** Current value (matched against `options[].value`). */
  value: string
  options: InlineSelectOption[]
  canEdit: boolean
  /** Persist the chosen value; the popover closes on resolve, toasts on reject. */
  onSave: (value: string) => Promise<unknown>
  /** Accessible name for the trigger (e.g. "Edit priority"). */
  ariaLabel: string
  /** The read display (chip / pill) shown when collapsed. */
  children: ReactNode
  errorMessage?: string
  variant?: "cell" | "panel"
}

/**
 * Click-to-edit single-select for small enum fields (prospect priority,
 * customer status / support level / business type). Renders the page's existing
 * read display; on click a popover lists the options and picking one commits
 * immediately. Picking the already-current value is a no-op (just closes).
 *
 * See docs/crm.md → "Inline editing".
 */
export function InlineSelectEditor({
  value,
  options,
  canEdit,
  onSave,
  ariaLabel,
  children,
  errorMessage,
  variant = "cell",
}: InlineSelectEditorProps) {
  return (
    <InlineEditField
      canEdit={canEdit}
      ariaLabel={ariaLabel}
      variant={variant}
      {...(errorMessage ? { errorMessage } : {})}
      renderEditor={({ close, commit, saving }) => (
        <MenuList dense disablePadding sx={{ py: 0.5 }}>
          {options.map((option) => {
            const selected = option.value === value
            return (
              <MenuItem
                key={option.value}
                selected={selected}
                disabled={saving}
                onClick={() => {
                  if (selected) {
                    close()
                    return
                  }
                  void commit(() => onSave(option.value))
                }}
              >
                <ListItemIcon sx={{ minWidth: 28, color: "primary.main" }}>
                  {selected ? <CheckIcon size={16} /> : null}
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: 14 }}>{option.label}</ListItemText>
              </MenuItem>
            )
          })}
        </MenuList>
      )}
    >
      {children}
    </InlineEditField>
  )
}
export default InlineSelectEditor
